import React, {
	useState,
	useEffect,
	useContext
} from 'react';

import {
	Routes,
	useNavigate
} from 'react-router-dom';

import {
	Box,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
	TableCell,
	TableRow,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadTwoToneIcon from '@mui/icons-material/DownloadTwoTone';
import { ANALYTICS_CONSOLE } from '../config/api';
import Footer from '../components/footer';
import axios from 'axios';
import ModalComponent from '../components/common/global-component/modal/modal-component';
import DialogComponent from '../components/common/global-component/dialog/dialog-component';
import ModalService from '../services/modal-service';
import { AppPermissionsContext } from '../components/common/app-context/app-permission-context';
import { getAnalyticsConsole, getDataSources, getReportList, getClients } from '../services/api-services'
import TableContent from '../components/common/pages/table-content-components';
import * as ROUTES from '../config/routes';
import { cbcrReportId, pillarTwoReportId } from '../config/constants';

const AnalyticConsole = () => {
	const [objects, setObjects] = useState([]);
	const { storedUser, permissions } = useContext(AppPermissionsContext);
	const [reportList, setReportList] = useState([]);
	const [datasources, setDatasources] = useState([]);
	const [clients, setClients] = useState([]);
	const [downloadingIds, setDownloadingIds] = useState(new Set());
	const navigate = useNavigate();

	const setDownloading = (id, busy) => setDownloadingIds(prev => {
		const next = new Set(prev);
		busy ? next.add(id) : next.delete(id);
		return next;
	});

	const isPillarTwo = window.location.pathname.includes('pillar-two/analytics-console');

	const handleDownload = async (row) => {
		setDownloading(row.id, true);
		try {
			const response = await axios.get(`${ANALYTICS_CONSOLE}/download/${row.id}`, {
				headers: {
					Authorization: `Bearer ${storedUser}`,
				},
				responseType: 'blob',
			});

			const url = window.URL.createObjectURL(response.data);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${row.name || 'analytics-console'}.pptx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading analytics console file:', error);
		} finally {
			setDownloading(row.id, false);
		}
	};

	const templateId = isPillarTwo
		? pillarTwoReportId
		: cbcrReportId;

	const screen = {
		type: 'Analytic Console',
		heading: {
			title: 'Analytics Console',
			description: isPillarTwo
				? 'Create a console based on data sources and get a comprehensive view over your Pillar Two data'
				: 'Create a console based on data sources and get a comprehensive view over your CbC Reporting'
		},
		search: {
			type: 'text',
			placeholder: 'Search by client.',
			fullWidth: true,
			size: 'small'
		},
		tableHead: [
			'Report Name',
			'Company',
			'Period',
			'Template',
			'Datasources',
			''
		]
	};

	const formControls = [
		{
			label: "Name",
			placeholder: "Your client name, please",
			helperText: "Name is required",
			required: true,
			validation: (obj) => true
		},
		{
			label: "Company",
			placeholder: "Select your company, please",
			name: "companyId",
			type: 'selectbox',
			options: clients,
		},
		{
			label: "Period",
			placeholder: "Select your period, please",
			name: "year",
			helperText: "Period is required",
			required: true,
			validation: (obj) => true
		},
		{
			label: "Template",
			placeholder: "Select your template, please",
			type: 'selectbox',
			options: reportList,
			name: "template",
			defaultValue: templateId
		},
		{
			label: "Datasources",
			placeholder: "Select your datasource, please",
			type: 'selectbox',
			options: datasources,
			name: "file"
		}
	]
	const createModal = {
		form: {
			formControls,
			processText: 'Add new console',
			title: 'Create New Console',
			validation: () => true,
			type: 'create',
		},
		request: {
			method: 'POST',
			requestValidation: () => true,
			url: () => `${ANALYTICS_CONSOLE}/create`,
		},
	}

	const editModal = {
		form: {
			description: 'Configure your console settings',
			formControls,
			processText: 'Edit existing client',
			title: 'Edit Existing Client',
			validation: () => true,
			type: 'edit'
		},
		request: {
			method: 'PATCH',
			requestValidation: () => modals.edit.modalData.selected,
			url: () => `${ANALYTICS_CONSOLE}/${modals.edit.modalData.selected.id}`,
		}
	}

	const deleteModal = {
		form: {
			description: 'Are you sure, you know what you doing?',
			formControls: [
				{
					label: "Name",
					type: 'readonly',
					disabled: true,
				},
				{
					label: "ID",
					type: 'readonly',
					disabled: true,
				}
			],
			processText: 'Delete existing client',
			title: 'Delete Existing Client',
			validation: () => true,
			type: 'delete'
		},
		request: {
			method: 'DELETE',
			requestValidation: () => modals.delete.modalData.selected.id,
			url: () => `${ANALYTICS_CONSOLE}/${modals.delete.modalData.selected.id}`,
		}
	}

	const modals = {
		create: ModalService(createModal),
		edit: ModalService(editModal),
		delete: ModalService(deleteModal),
	}

	const validPbiTemplates = [
		{
			id: cbcrReportId,
			name: 'CbCR Template'
		},
		{
			id: pillarTwoReportId,
			name: 'Pillar Two Template'
		}
	];

	useEffect(() => {
		setReportList(validPbiTemplates);
		getDataSources(storedUser, templateId)
			.then(response => {
				setDatasources(response.data);
			});

		getClients(storedUser)
			.then(response => {
				setClients(response.data);
			});
	}, [navigate, modals.delete.result, modals.edit.result, modals.create.result]);

	useEffect(() => {
		getAnalyticsConsole(storedUser, templateId)
			.then(response => {
				const data = Array.isArray(response.data) ? response.data : [];
				console.log('[AnalyticConsole] fetched', data.length, 'records', data);
				const mapped = data.map(f => ({ ...f, templateName: getCurrentTemplate(f.template) }));
				setObjects(mapped);
			})
			.catch(err => console.error('[AnalyticConsole] fetch error', err));
	}, [reportList]);

	const actions = [
		{
			icon: <AddIcon />,
			name: 'Add New Console',
			callback: (e) => modals.create.open(e, { template: templateId })
		}
	];

	const filterOptions = (obj, _, searchValue) => {
		return obj?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
			obj?.client?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
			obj?.templateName?.toLowerCase().includes(searchValue.toLowerCase()) ||
			obj?.year?.toLowerCase().includes(searchValue.toLowerCase())
	}

	const getCurrentTemplate = (id) => reportList.find(f => f.id == id)?.name ?? 'N\A';

	const tableBodyMapper = (f) => (
		<TableRow key={`${f.id}_row`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
			{/* <TableCell key={`${f.id}_id`}>{f.id}</TableCell> */}
			<TableCell key={`${f.id}_name`}>{f.name}</TableCell>
			<TableCell key={`${f.id}_company`}>{f.client?.name}</TableCell>
			<TableCell key={`${f.id}_period`}>{f.year}</TableCell>
			<TableCell key={`${f.id}_template`}>{f.templateName}</TableCell>
			<TableCell key={`${f.id}_datasources`}>{f.datasource?.file}</TableCell>

			<TableCell key={`${f.id}_actions`}>
				<Typography key={`${f.id}_typo`}>
					{permissions && permissions['Analytics Console'] && permissions['Analytics Console'].read && <Tooltip title="View analytic console" arrow><IconButton key={`${f.id}_show`}
						onClick={(e) => navigate(`${ROUTES.ANALYTIC_CONSOLES_SHOW}/${f.id}` + (f.template == pillarTwoReportId ? '#pillar-two' : ''))}>
						<VisibilityIcon />
					</IconButton></Tooltip>
					}
					{permissions && permissions['Analytics Console'] && permissions['Analytics Console'].update && false && <Tooltip title="Edit analytic console" arrow><IconButton key={`${f.id}_edit`}
						onClick={(e) => modals.edit.open(e, f)}>
						<EditTwoToneIcon />
					</IconButton></Tooltip>
					}
					{permissions && permissions['Analytics Console'] && permissions['Analytics Console'].delete && <Tooltip title="Delete analytic console" arrow><IconButton key={`${f.id}_delete`}
						onClick={(e) => modals.delete.open(e, f)}>
						<DeleteTwoToneIcon />
					</IconButton></Tooltip>
					}
					{permissions && permissions['Analytics Console'] && permissions['Analytics Console'].read && (
						<Tooltip title="Download as PowerPoint (.pptx)" arrow>
							<IconButton
								key={`${f.id}_download`}
								disabled={downloadingIds.has(f.id)}
								onClick={() => handleDownload(f)}
							>
								{downloadingIds.has(f.id) ? <CircularProgress size={20} /> : <DownloadTwoToneIcon />}
							</IconButton>
						</Tooltip>
					)}
				</Typography>
			</TableCell>
		</TableRow >
	);

	const getModalComponent = (modalData) => {
		const validation = modalData && modalData.validation && modalData.validation();
		return (validation && <ModalComponent key={modalData.type} data={modalData}>
			<Box>
				<form noValidate autoComplete='off'>
					{
						<DialogComponent key={modalData.type} data={{ ...modalData, defaultValue: modalData.selected }}></DialogComponent>
					}
				</form>
			</Box>
		</ModalComponent>
		)
	};

	return (
		<React.Fragment>
			<TableContent data={{
				area: 'Analytics Console',
				objects,
				body: tableBodyMapper,
				filterOptions,
				screen
			}}>
				<Footer actions={permissions?.Users?.create && actions} />
				{
					[
						getModalComponent(modals.create.modalData),
						getModalComponent(modals.edit.modalData),
						getModalComponent(modals.delete.modalData)
					]
				}
			</TableContent>
		</React.Fragment>
	);
}

export default AnalyticConsole;